<script lang="ts">
	import { ClientVelatlasLoggerApi } from '$lib/api/velatlas/velatlas.loggerApi.client';
	import type { WikiMarker } from '$lib/api/wiki/response.types';
	import {
		setWikiUrlResolution,
		wikiGeoSearch,
		wikiGetBatchSummaries,
		wikiGetPageMediaList,
		type WikiImageSize
	} from '$lib/api/wiki/wiki.api';
	import type { DiscoveredPoi, DiscoveredPoiDetails } from '$lib/components/ui/map/_other/discoveredPoi';
	import type { TourWikiCache } from '$lib/components/ui/map/_other/tourWikiCache';
	import WikiMapIcon from '$lib/components/ui/map/WikiMapIcon.svelte';
	import {
		createCoordinateKey,
		flattenCoordinates,
		type Coordinates,
		type FlatCoordinates
	} from '$lib/features/_other/coordinates.type';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import { CITY_FEATURE_PAGE_PER_ITEM_TYPES } from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { Route } from '$lib/features/route/route.type';
	import type { TourStop } from '$lib/features/tour/stop/tourStop.type';
	import { deviceScreen } from '$lib/state/deviceScreen.svelte';
	import { ANALYTICS_EVENT_NAMES, WIKI_POINTS_SETTINGS } from '$lib/statics';
	import { getStreetViewUrl, metersToMapPixels } from '$lib/utils/geo.util';
	import {
		calculateDistance,
		extractRouteCoordinates,
		getBearing,
		getHaversineDistanceMeters,
		PolylineSampler,
		type RoutePointSample
	} from '$lib/utils/geoInterpolation.util';
	import { useBackButtonInterception } from '$lib/utils/history.util.svelte';
	import { UrlUtil, type NavLink } from '$lib/utils/url.util';
	import type { Map as LibreMap } from 'maplibre-gl';
	import { onMount, untrack } from 'svelte';
	import { Marker } from 'svelte-maplibre-gl';
	import { fade, fly, slide } from 'svelte/transition';
	import IconArrowRight from '~icons/mdi/arrow-right';
	import IconChevronDown from '~icons/mdi/chevron-down';
	import IconChevronLeft from '~icons/mdi/chevron-left';
	import IconChevronRight from '~icons/mdi/chevron-right';
	import IconClose from '~icons/mdi/close';
	import IconFlame from '~icons/mdi/flame';
	import IconStreetView from '~icons/mdi/google-street-view';
	import IconHistory from '~icons/mdi/history';
	import IconNavigation from '~icons/mdi/navigation';
	import IconPause from '~icons/mdi/pause';
	import IconPlay from '~icons/mdi/play';
	import IconRadar from '~icons/mdi/radar';
	import IconRestart from '~icons/mdi/restart';
	import IconTune from '~icons/mdi/tune';
	import IconWikipedia from '~icons/mdi/wikipedia';

	interface PlayerState {
		isPlaying: boolean;
		currentBearing: number;
		discoveredDrawer: {
			isShowing: boolean;
			playerPlayingBeforeShowing: boolean;
		};
		settings: {
			show: boolean;
			playbackSpeed: ALLOWED_SPEED;
			discoveryRadiusMeters: number;
		};
	}
	interface UpcomingPoi {
		title: string;
		distance: number;
	}
	interface StopCheckpoint {
		label: string;
		index: number;
		distance: number;
		progress: number;
	}
	interface InspectionState {
		poi: DiscoveredPoi | null;
		playerPlayingBeforeInspection: boolean;
	}
	interface PassingByPoiPanelState {
		poi: DiscoveredPoi | null;
		isExpanded: boolean;
		playerPlayingBeforeExpanding: boolean;
	}
	interface RadarWaveState {
		key: number;
		isActive: boolean;
	}

	let {
		map,
		location,
		route,
		stops = [],
		initialDistance = 0,
		cache,
		onProgressChange,
		onClose
	}: {
		map: LibreMap;
		location: PlaceLocation;
		route: Route;
		stops?: TourStop[];
		initialDistance?: number;
		cache: TourWikiCache;
		onProgressChange?: (distance: number) => void;
		onClose?: () => void;
	} = $props();

	const analyticsApi = new ClientVelatlasLoggerApi();
	const MAX_IMAGES: number = 4;
	const LANDMARK_PERIMETER_DISTANCE_METERS: number = 30;
	const NEARBY_LANDMARK_DISTANCE_METERS: number = 600;
	const BASE_WALK_SPEED_MPS: number = 2.8; // Base walk speed: 2.8 m/s (approx 10 km/h)
	type ALLOWED_SPEED = 0.5 | 1 | 2 | 4 | 6;
	const SPEED_MULTIPLIERS: ALLOWED_SPEED[] = [0.5, 1, 2, 4, 6];
	const STEP_M = 5;
	const PLAYER_ZOOM = 17.8;
	let lastParentSync: number = 0;

	let dialogEl = $state<HTMLDialogElement | null>(null);
	const player = $state<PlayerState>({
		isPlaying: true,
		currentBearing: 0,
		discoveredDrawer: {
			isShowing: false,
			playerPlayingBeforeShowing: false
		},
		settings: {
			show: false,
			playbackSpeed: 1,
			discoveryRadiusMeters: 200
		}
	});
	const inspection = $state<InspectionState>({
		poi: null,
		playerPlayingBeforeInspection: false
	});
	let expandedPoi = $state<DiscoveredPoi | null>(null);
	const passingByPoiPanel = $state<PassingByPoiPanelState>({
		poi: null,
		isExpanded: false,
		playerPlayingBeforeExpanding: false
	});
	const radarWave = $state<RadarWaveState>({ key: 0, isActive: false });
	let currentDistance = $state(untrack(() => initialDistance));

	const coordinates = $derived<FlatCoordinates[]>(extractRouteCoordinates(route, stops));
	const sampler = $derived<PolylineSampler>(new PolylineSampler(coordinates));
	const routeScan = $derived.by(() => {
		const total = sampler.totalDistance;
		if (total <= 0) return [];
		const steps = Math.max(50, Math.ceil(total / 20));
		return Array.from({ length: steps + 1 }, (_, i) => {
			const distance = (total * i) / steps;
			return { distance, coordinates: sampler.sampleAtDistance(distance).coordinates };
		});
	});
	const coarseDistance = $derived<number>(Math.floor(currentDistance / STEP_M) * STEP_M);
	const coarseSample = $derived<RoutePointSample>(sampler.sampleAtDistance(coarseDistance));
	const displayDistance = $derived<number>(Math.round(currentDistance));
	const currentSample = $derived<RoutePointSample>(sampler.sampleAtDistance(currentDistance));
	const stopCheckpoints = $derived.by<StopCheckpoint[]>(() => {
		if (!stops.length || !sampler.totalDistance) return [];
		return stops.map((stop, index): StopCheckpoint => {
			const { distance } = findClosestOnRoute(flattenCoordinates(stop.place.coordinates));
			return {
				label: stop.englishName,
				index,
				distance,
				progress: distance / sampler.totalDistance
			};
		});
	});
	const allRouteLandmarks = $derived.by<DiscoveredPoi[]>(() => {
		const list: DiscoveredPoi[] = [];
		const seenTitles = new Set<string>();
		const matchedStopNames = new Set<string>();

		for (const wikiPoi of cache.landmarks.values()) {
			if (seenTitles.has(wikiPoi.marker.title)) continue;
			seenTitles.add(wikiPoi.marker.title);
			const wikiCoord = flattenCoordinates(wikiPoi.marker.coordinates);

			const stopMatchingWikiLandmark = stops.find((s) => {
				if (s.place.wikidataId && wikiPoi.wikidataId) {
					return s.place.wikidataId === wikiPoi.wikidataId;
				}
				// Fallback only if wikidataId is missing on either side:
				const stopCoord = flattenCoordinates(s.place.coordinates);
				return getHaversineDistanceMeters(wikiCoord, stopCoord) <= 15;
			});

			if (stopMatchingWikiLandmark) {
				matchedStopNames.add(stopMatchingWikiLandmark.englishName);

				const stopImageUrls = [...(stopMatchingWikiLandmark.images || []), ...(stopMatchingWikiLandmark.primary?.images || [])]
					.slice(0, MAX_IMAGES)
					.map((img) => UrlUtil.getFullImageUrl(img));
				const wikiImagesUrls = wikiPoi.details?.images?.length
					? wikiPoi.details.images.slice(0, MAX_IMAGES)
					: wikiPoi.marker.image
						? [wikiPoi.marker.image]
						: [];
				const chosenImages = stopImageUrls.length >= wikiImagesUrls.length ? stopImageUrls : wikiImagesUrls;

				list.push({
					...wikiPoi,
					stop: stopMatchingWikiLandmark,
					isWikiArticle: true,
					marker: {
						...wikiPoi.marker,
						image: chosenImages[0] ?? wikiPoi.marker.image
					},
					details: {
						images: chosenImages,
						description: wikiPoi.details?.description ?? stopMatchingWikiLandmark.description,
						subtitle: wikiPoi.details?.subtitle ?? stopMatchingWikiLandmark.nativeName
					}
				});
			} else {
				list.push({ ...wikiPoi, isWikiArticle: true });
			}
		}

		// Add Tour Stops without Wiki articles (skip if already matched to a Wiki article above)
		for (const checkpoint of stopCheckpoints) {
			const stop = stops[checkpoint.index];
			if (!stop) continue;

			const isNotStandaloneStop: boolean = matchedStopNames.has(stop.englishName) || seenTitles.has(stop.englishName);
			if (isNotStandaloneStop) continue;
			seenTitles.add(stop.englishName);

			const imageUrls = [...(stop.images || []), ...(stop.primary?.images || [])]
				.slice(0, MAX_IMAGES)
				.map((img) => UrlUtil.getFullImageUrl(img));

			list.push({
				marker: {
					title: stop.englishName,
					coordinates: stop.place.coordinates,
					image: imageUrls.at(0) ?? null
				},
				routeDistance: checkpoint.distance,
				stop,
				isWikiArticle: false,
				details: {
					images: imageUrls,
					description: stop.description,
					subtitle: stop.nativeName
				}
			});
		}
		return list;
	});
	const passingByPoi = $derived.by<DiscoveredPoi | null>(() => {
		let closest: DiscoveredPoi | null = null;
		let minDiff = Infinity;

		for (const poi of allRouteLandmarks) {
			const approachStart = poi.routeDistance - player.settings.discoveryRadiusMeters;
			const passEnd = poi.routeDistance + LANDMARK_PERIMETER_DISTANCE_METERS;
			const isWithinLandmarkPerimeter = coarseDistance >= approachStart && coarseDistance <= passEnd;
			if (isWithinLandmarkPerimeter) {
				const diff = Math.abs(coarseDistance - poi.routeDistance);
				if (diff < minDiff) {
					minDiff = diff;
					closest = poi;
				}
			}
		}

		return closest;
	});
	const pastDiscoveredPois = $derived<DiscoveredPoi[]>(
		allRouteLandmarks
			.filter((p) => coarseDistance > p.routeDistance + LANDMARK_PERIMETER_DISTANCE_METERS)
			.sort((a, b) => b.routeDistance - a.routeDistance)
	);
	const visibleMapLandmarks = $derived<DiscoveredPoi[]>(
		allRouteLandmarks.filter((p) => coarseDistance >= p.routeDistance - player.settings.discoveryRadiusMeters)
	);
	const upcomingLandmark = $derived.by<UpcomingPoi | null>(() => {
		let closest: { title: string; distance: number } | null = null;
		let minDistance = Infinity;

		for (const poi of allRouteLandmarks) {
			const distAhead = poi.routeDistance - coarseDistance;
			const isWithinDiscoveryPerimeter =
				distAhead > player.settings.discoveryRadiusMeters &&
				distAhead <= NEARBY_LANDMARK_DISTANCE_METERS &&
				distAhead < minDistance;
			if (isWithinDiscoveryPerimeter) {
				minDistance = distAhead;
				closest = { title: poi.marker.title, distance: Math.round(distAhead) };
			}
		}

		return closest;
	});
	const radarDiameterPx = $derived(
		Math.round(metersToMapPixels(player.settings.discoveryRadiusMeters, coordinates[0]?.[1] ?? 0, PLAYER_ZOOM) * 2)
	);
	const popularLandmarkTitles = $derived.by<Set<string>>(() => {
		const landmarks = Array.from(cache.landmarks.values());
		if (landmarks.length < 3) return new Set();

		const viewCounts = landmarks.map((l) => l.pageViews ?? 0).sort((a, b) => a - b);
		const percentileIndex = Math.floor(viewCounts.length * WIKI_POINTS_SETTINGS.topArticlesPercentile);
		const threshold = viewCounts[percentileIndex];

		if (threshold <= 0) return new Set();

		const topTitles = new Set<string>();
		for (const l of landmarks) {
			if ((l.pageViews ?? 0) >= threshold) {
				topTitles.add(l.marker.title);
			}
		}
		return topTitles;
	});

	useBackButtonInterception({
		stateKey: 'gps-player-inspection-modal',
		active: () => inspection.poi !== null,
		enabled: () => deviceScreen.smallerThanDesktop,
		getData: () => inspection.poi,
		onBack: () => closeInspection(),
		onForward: (poi) => {
			if (poi !== null) {
				inspectLandmark(poi);
			}
		}
	});

	useBackButtonInterception({
		stateKey: 'gps-player-passing-by-poi-expanded',
		active: () => passingByPoiPanel.isExpanded,
		enabled: () => deviceScreen.smallerThanDesktop,
		onBack: () => togglePassingByPoiPanel(),
		onForward: () => togglePassingByPoiPanel()
	});

	useBackButtonInterception({
		stateKey: 'gps-player-mobile-drawer',
		active: () => player.discoveredDrawer.isShowing,
		enabled: () => deviceScreen.isMobile,
		onBack: () => (player.discoveredDrawer.isShowing = false),
		onForward: () => (player.discoveredDrawer.isShowing = true)
	});

	onMount(() => {
		const startTime = Date.now();
		const navbar = document.getElementById('navbar');
		if (navbar) {
			navbar.style.display = 'none';
		}

		const initialSample = sampler.sampleAtDistance(currentDistance);

		const originalRatio = window.devicePixelRatio;
		map.setPixelRatio?.(Math.min(originalRatio, deviceScreen.isMobile ? 1.5 : 2));

		updateCamera(initialSample, 0.016, true);

		if (currentDistance > 0) {
			handleSeek(currentDistance);
		}

		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose?.();
			}
		};

		window.addEventListener('keydown', onKeyDown);

		return () => {
			if (navbar) {
				navbar.style.display = '';
			}
			const durationSec = Math.round((Date.now() - startTime) / 1000);
			analyticsApi.sendAnalytics({
				event: ANALYTICS_EVENT_NAMES.tourPlayer,
				payload: { durationSec, speed: player.settings.playbackSpeed, discoveryRadius: player.settings.discoveryRadiusMeters }
			});
			map.setPixelRatio?.(originalRatio);
			window.removeEventListener('keydown', onKeyDown);
		};
	});

	$effect(() => {
		if (!passingByPoiPanel.isExpanded) {
			passingByPoiPanel.poi = passingByPoi;
		}
	});

	$effect(() => {
		if (!player.isPlaying) return; // no rAF at all while paused

		const minFrameMs = 1000 / 60 - 2; // tolerance so vsync jitter doesn't skip to every 3rd frame
		let id = 0;
		let lastRender = performance.now();
		let lastProximityDist = -Infinity;

		function frame(now: number) {
			id = requestAnimationFrame(frame);
			if (now - lastRender < minFrameMs) return;
			const dt = Math.min((now - lastRender) / 1000, 0.1);
			lastRender = now;

			if (sampler.totalDistance <= 0) return;
			currentDistance = Math.min(
				currentDistance + BASE_WALK_SPEED_MPS * player.settings.playbackSpeed * dt * 4,
				sampler.totalDistance
			);

			const sample = sampler.sampleAtDistance(currentDistance);
			updateCamera(sample, dt);

			if (Math.abs(currentDistance - lastProximityDist) >= 25) {
				lastProximityDist = currentDistance;
				checkNearbyWikipedia(sample.coordinates);
			}
			syncProgressToParent(false);

			if (currentDistance >= sampler.totalDistance) {
				player.isPlaying = false; // effect re-runs and cleans up the loop
				syncProgressToParent(true);
			}
		}

		id = requestAnimationFrame(frame);
		return () => cancelAnimationFrame(id);
	});

	$effect(() => {
		if (dialogEl && inspection.poi) {
			dialogEl.showModal();
		}
	});

	function inspectLandmark(poi: DiscoveredPoi) {
		inspection.playerPlayingBeforeInspection = player.isPlaying;
		player.isPlaying = false;
		syncProgressToParent(true);
		inspection.poi = poi;
		analyticsApi.sendAnalytics({
			event: ANALYTICS_EVENT_NAMES.tourPlayer,
			payload: { poiSource: 'inspection' }
		});
	}

	function closeInspection(): void {
		inspection.poi = null;
		player.isPlaying = inspection.playerPlayingBeforeInspection;
	}

	function togglePassingByPoiPanel(): void {
		if (passingByPoiPanel.isExpanded) {
			passingByPoiPanel.isExpanded = false;
			if (passingByPoiPanel.playerPlayingBeforeExpanding) {
				player.isPlaying = true;
			}
		} else {
			passingByPoiPanel.playerPlayingBeforeExpanding = player.isPlaying;
			player.isPlaying = false;
			passingByPoiPanel.isExpanded = true;

			analyticsApi.sendAnalytics({
				event: ANALYTICS_EVENT_NAMES.tourPlayer,
				payload: { poiSource: 'passingBy' }
			});
		}
	}

	function toggleExpanded(poi: DiscoveredPoi): void {
		const tappedSameExpandedPoi = expandedPoi?.marker.title === poi.marker.title;
		if (expandedPoi === null || !tappedSameExpandedPoi) {
			expandedPoi = poi;
			analyticsApi.sendAnalytics({
				event: ANALYTICS_EVENT_NAMES.tourPlayer,
				payload: { poiSource: 'expanded' }
			});
		} else if (expandedPoi.marker.title === poi.marker.title) {
			expandedPoi = null;
		}
	}

	function updateCamera(sample: RoutePointSample, dt = 0.016, immediate = false) {
		if (!Number.isFinite(sample.coordinates[0]) || !Number.isFinite(sample.coordinates[1])) return;

		// Target Bearing using 25m Forward Look-Ahead
		let targetBearing = sample.bearing;
		const lookAheadDist = 25;
		if (currentDistance + 2 < sampler.totalDistance) {
			const aheadSample = sampler.sampleAtDistance(Math.min(currentDistance + lookAheadDist, sampler.totalDistance));
			targetBearing = getBearing(sample.coordinates, aheadSample.coordinates);
		}

		// Exponential Spring Easing
		if (immediate) {
			player.currentBearing = targetBearing;
		} else {
			let angleDiff = ((targetBearing - player.currentBearing + 540) % 360) - 180;
			const smoothingFactor = 1 - Math.exp(-2.2 * dt);
			player.currentBearing = (player.currentBearing + angleDiff * smoothingFactor + 360) % 360;
		}

		map.jumpTo({
			center: sample.coordinates,
			bearing: player.currentBearing,
			pitch: 62,
			zoom: PLAYER_ZOOM,
			padding: { bottom: 220, top: 0, left: 0, right: 0 }
		});
	}

	function getRouteIntersection(marker: WikiMarker): { routeDistance: number; minSeparation: number } {
		const { distance, separation } = findClosestOnRoute(flattenCoordinates(marker.coordinates));
		return { routeDistance: Math.round(distance), minSeparation: Math.round(separation) };
	}

	async function prefetchLandmarkDetails(title: string): Promise<DiscoveredPoiDetails | null> {
		const cached = cache.details.get(title);
		if (cache.galleryFetched.has(title)) {
			return cached ?? null;
		}

		return cache.dedupe(`gallery:${title}`, async () => {
			try {
				const gallery = await wikiGetPageMediaList(title, { maxImages: MAX_IMAGES, imageSize: 60 });
				const existing = cache.details.get(title);
				const payload: DiscoveredPoiDetails = {
					images: gallery.length > 0 ? gallery : (existing?.images ?? []),
					description: existing?.description ?? null,
					subtitle: existing?.subtitle ?? null
				};
				cache.details.set(title, payload);
				cache.galleryFetched.add(title); // even if it only had 0–1 images

				const existingPoi = cache.landmarks.get(title);
				if (existingPoi) cache.landmarks.set(title, { ...existingPoi, details: payload });
				return payload;
			} catch (err) {
				console.debug(`Wikipedia details fetch failed for '${title}'`, err);
				return null; // not marked as fetched, so it may retry later
			}
		});
	}

	async function checkNearbyWikipedia(pos: FlatCoordinates) {
		const alreadyCovered = cache.searchedCenters.some(
			(c) => getHaversineDistanceMeters(c, pos) < NEARBY_LANDMARK_DISTANCE_METERS
		);
		if (alreadyCovered || cache.geoSearchInFlight) return;

		cache.geoSearchInFlight = true;
		try {
			const knownTitles = new Set([...cache.landmarks.keys(), ...cache.rejected]);
			const results = await wikiGeoSearch({ centre: { lat: pos[1], lon: pos[0] }, cachedWikiItemTitles: knownTitles });
			cache.searchedCenters.push(pos); // only after success

			const candidates: { marker: WikiMarker; routeDistance: number }[] = [];
			for (const r of results) {
				if (cache.landmarks.has(r.title) || cache.rejected.has(r.title)) continue;
				const { routeDistance, minSeparation } = getRouteIntersection(r);
				if (minSeparation > player.settings.discoveryRadiusMeters * 1.35) continue;
				candidates.push({ marker: r, routeDistance });
			}

			const titlesToBatch = candidates.map((c) => c.marker.title).filter((t) => !cache.details.has(t));
			const batchSummaries = titlesToBatch.length
				? await wikiGetBatchSummaries(titlesToBatch, { imageSize: 500 })
				: new Map<string, Awaited<ReturnType<typeof wikiGetBatchSummaries>> extends Map<string, infer V> ? V : never>();

			for (const [title, summary] of batchSummaries) {
				if (!cache.details.has(title)) {
					cache.details.set(title, {
						images: summary.thumbnail ? [summary.thumbnail] : [],
						description: summary.description,
						subtitle: summary.subtitle
					});
				}
			}

			for (const { marker: r, routeDistance } of candidates) {
				void (async () => {
					const details = await prefetchLandmarkDetails(r.title);
					if (!details) return;
					if (details.images.length === 0) {
						cache.rejected.add(r.title);
						return;
					}

					const key = createCoordinateKey(r.coordinates);
					const existingWiki = [...cache.landmarks.values()].find((p) => createCoordinateKey(p.marker.coordinates) === key);
					if (existingWiki) {
						if ((existingWiki.details?.images?.length ?? 0) >= details.images.length) return;
						cache.landmarks.delete(existingWiki.marker.title);
					}

					const summary = batchSummaries.get(r.title);
					cache.landmarks.set(r.title, {
						wikidataId: summary?.wikidataId ?? null,
						marker: r,
						routeDistance,
						details,
						isWikiArticle: true,
						pageViews: summary?.pageViews ?? 0
					});
				})();
			}
		} catch (err) {
			console.warn('Wiki geosearch error:', err);
		} finally {
			cache.geoSearchInFlight = false;
		}
	}

	function handleSeek(targetDistance: number) {
		currentDistance = Math.max(0, Math.min(targetDistance, sampler.totalDistance));
		syncProgressToParent(true);

		const sample = sampler.sampleAtDistance(currentDistance);
		updateCamera(sample, 0.016, true);
		checkNearbyWikipedia(sample.coordinates);
	}

	function getLiveDistanceToMarker(marker: WikiMarker | { coordinates: Coordinates }): number {
		return calculateDistance(coarseSample.coordinates, flattenCoordinates(marker.coordinates));
	}

	function syncProgressToParent(force = false) {
		const now = performance.now();
		const beenLongEnough = now - lastParentSync > 2500;
		if (force || beenLongEnough) {
			lastParentSync = now;
			onProgressChange?.(Math.round(currentDistance));
		}
	}

	function findClosestOnRoute(target: FlatCoordinates): { distance: number; separation: number } {
		let bestDistance = 0;
		let minSeparation = Infinity;
		for (const point of routeScan) {
			const sep = getHaversineDistanceMeters(point.coordinates, target);
			if (sep < minSeparation) {
				minSeparation = sep;
				bestDistance = point.distance;
			}
		}
		return { distance: bestDistance, separation: minSeparation };
	}

	function generateSpecificSizeImageUrls({
		poi,
		singleImageSize,
		multiImagesSize
	}: {
		poi: DiscoveredPoi;
		singleImageSize: WikiImageSize;
		multiImagesSize: WikiImageSize;
	}): string[] {
		const detailsImages: string[] = poi.details?.images ?? [];
		const markerImages: string[] = [poi.marker.image ?? null].filter((imageUrl) => imageUrl !== null);
		const images: string[] = detailsImages.length > 0 ? detailsImages : markerImages;
		if (images.length === 0) return [];
		if (images.length === 1) return images.map((imageUrl) => setWikiUrlResolution(imageUrl, singleImageSize));
		return images.map((imageUrl) => setWikiUrlResolution(imageUrl, multiImagesSize));
	}

	function triggerRadarWave() {
		radarWave.key++;
		radarWave.isActive = true;
	}
</script>

{@render CurrentLocationMarker(currentSample)}

{#each visibleMapLandmarks as poi (poi.marker.title)}
	{@render MapLandmarkIcon(poi)}
{/each}

<div class="pointer-events-none absolute inset-0 z-120 overflow-hidden font-sans select-none">
	{@render HudOverlay()}
</div>

{#if inspection.poi}
	{@render InspectionModal(inspection.poi)}
{/if}

{#if deviceScreen.isMobile}
	{#if player.discoveredDrawer.isShowing}
		<div
			class="pointer-events-auto fixed inset-0 z-120 flex flex-col overflow-hidden overscroll-contain bg-base-100 p-4 select-text md:hidden"
			role="dialog"
			aria-modal="true"
		>
			{@render DiscoveredLandmarksDrawer()}
		</div>
	{/if}
{:else}
	<aside
		class="pointer-events-auto fixed inset-y-0 left-0 z-120 min-w-67.5 flex-col overflow-hidden rounded-tr-box bg-base-300/93 md:flex md:w-[20vw]"
	>
		{@render DiscoveredLandmarksDrawer()}
	</aside>
{/if}

{#snippet CurrentLocationMarker(current: RoutePointSample)}
	<Marker lnglat={current.coordinates} pitchAlignment="map" rotationAlignment="map">
		{#snippet content()}
			<div class="pointer-events-none relative flex size-14 items-center justify-center">
				{#if radarWave.isActive && radarDiameterPx > 0}
					{#key radarWave.key}
						{const activeWaveSize = $derived(radarDiameterPx)}

						<div
							class="sonar-wave pointer-events-none absolute rounded-full border-2 border-primary bg-primary/20"
							style:width="{activeWaveSize}px"
							style:height="{activeWaveSize}px"
							onanimationend={() => (radarWave.isActive = false)}
						></div>
					{/key}
				{/if}

				<div class="absolute inset-0 animate-ping rounded-full bg-primary/30" style:animation-duration="2.2s"></div>
				<div class="absolute size-11" style:transform="rotate({player.currentBearing}deg)">
					<IconNavigation class="size-full text-primary" />
				</div>
			</div>
		{/snippet}
	</Marker>
{/snippet}

{#snippet HudOverlay()}
	<footer
		class="pointer-events-none absolute inset-x-0 bottom-11 z-100 flex flex-col items-center gap-2.5 p-3 sm:p-4 md:right-0 md:bottom-6 md:left-67.5 lg:bottom-0 lg:left-[20vw] xl:left-[15vw]"
	>
		{#if passingByPoi && !player.settings.show && deviceScreen.isMobile}
			<div class="grid w-full max-w-md overflow-hidden md:hidden">
				{const poiToUse = $derived.by<DiscoveredPoi>(() => {
					// if we just used passingByPoi and instantly expanded it on player load, it would get overriden as the wiki geo request finishes up, we so check if it's not expanded before changing it
					return passingByPoiPanel.poi ?? passingByPoi;
				})}
				{#key poiToUse.marker.title}
					{@render PassingByCardMobile(poiToUse)}
				{/key}
			</div>
		{/if}

		{#if player.settings.show}
			{@render Settings()}
		{/if}

		<div
			class="pointer-events-auto relative flex w-full max-w-full flex-col gap-1.5 rounded-box border border-base-200/20 bg-base-300/93 p-2.5 sm:gap-3 sm:p-4 md:max-w-lg lg:max-w-2xl"
		>
			<div class="flex items-center justify-between border-b border-base-content/15 pb-1.5 sm:pb-2">
				{@render Telemetry()}
			</div>

			{#if deviceScreen.isMobile}
				{@render NextStopSection()}
			{/if}

			<div class="relative flex w-full items-center py-3">
				{@render ScrubberTrack()}
			</div>

			<div class="flex min-w-0 flex-col items-center justify-between gap-1.5 pt-0.5 sm:gap-2 md:flex-row">
				{@render Controls()}
			</div>
		</div>
	</footer>

	{#snippet PassingByCardMobile(poi: DiscoveredPoi)}
		{const isPopular = $derived<boolean>(popularLandmarkTitles.has(poi.marker.title))}
		{const imageUrls = $derived<string[]>(
			generateSpecificSizeImageUrls({ poi: poi, singleImageSize: 250, multiImagesSize: 250 })
		)}

		<div
			class={{
				'pointer-events-auto flex w-full flex-col rounded-box bg-base-100/73 p-3 transition-all duration-300': true,
				'max-h-[75vh] gap-2.5 overflow-y-auto': passingByPoiPanel.isExpanded,
				'cursor-pointer gap-2': !passingByPoiPanel.isExpanded
			}}
			style="grid-area: 1 / 1 / 2 / 2;"
			in:fly={{ x: 120, duration: 320 }}
			out:fly={{ x: -120, duration: 280 }}
			role={passingByPoiPanel.isExpanded ? 'region' : 'button'}
			aria-label="{poi.marker.title} card"
			aria-expanded={passingByPoiPanel.isExpanded}
			onclick={togglePassingByPoiPanel}
		>
			{@render Header()}

			{#if passingByPoiPanel.isExpanded}
				<div role="none" tabindex="-1" onclick={(e) => e.stopPropagation()}>
					{@render ImagesCarousel(
						imageUrls.map((url) => setWikiUrlResolution(url, 330)),
						poi.marker.title,
						'h-[25vh]'
					)}
				</div>
			{:else}
				{@render ImagesRow(imageUrls)}
			{/if}

			{#if passingByPoiPanel.isExpanded}
				{@render ExpandedSection()}
			{/if}
		</div>

		{#snippet Header()}
			<div class="flex items-center justify-between gap-2">
				<div class="flex min-w-0 items-center gap-2">
					{@render PulsingDot()}
					<div class="flex min-w-0 flex-col">
						<span class="line-clamp-1 text-xs font-bold text-base-content">
							{poi.marker.title}
						</span>
						<span class="line-clamp-1 font-mono text-xs text-base-content/90">
							{getLiveDistanceToMarker(poi.marker)}m away
							{#if poi.details?.subtitle}
								· <i>{poi.details.subtitle}</i>
							{/if}
						</span>
					</div>
				</div>

				{#if passingByPoiPanel.isExpanded}
					<button
						class="btn btn-circle btn-ghost btn-xs"
						onclick={(e) => {
							e.stopPropagation();
							togglePassingByPoiPanel();
						}}
						aria-label="Collapse card"
					>
						<IconChevronDown class="text-base" />
					</button>
				{/if}
			</div>

			{#snippet PulsingDot()}
				{#if isPopular}
					<span class="relative flex size-4 shrink-0 items-center justify-center">
						<IconFlame class="relative size-full animate-pulse text-highlight" />
					</span>
				{:else}
					<span class="relative flex size-2 shrink-0">
						<span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75"></span>
						<span class="relative inline-flex size-2 rounded-full bg-primary"></span>
					</span>
				{/if}
			{/snippet}
		{/snippet}

		{#snippet ImagesRow(imageUrls: string[])}
			<div class="join mt-2 grid h-[10vh] grid-cols-4 overflow-hidden">
				{#each imageUrls as img (img)}
					<div class="join-item block h-full w-full overflow-hidden">
						<img class="h-full w-full object-cover" src={img} alt={poi.marker.title} />
					</div>
				{/each}
			</div>
		{/snippet}

		{#snippet ExpandedSection()}
			{#if poi.details?.description}
				<p class="line-clamp-5 text-xs leading-relaxed font-light text-base-content">
					{poi.details.description}
				</p>
			{/if}

			<div class="border-t border-base-content/10 pt-1">
				{@render ActionButtons(poi, true)}
			</div>
		{/snippet}
	{/snippet}

	{#snippet Settings()}
		<div
			class="pointer-events-auto mb-2 w-full rounded-box border border-white/20 bg-base-100/90 p-4 shadow-lg backdrop-blur-xl sm:self-center md:w-[50vw] lg:w-[30vw]"
			transition:fly={{ y: 20, duration: 200 }}
		>
			<div class="mb-3 flex items-center justify-between">
				<h4 class="text-xs text-base-content">DISCOVERY RADIUS</h4>
				<button class="btn btn-circle btn-ghost btn-xs" onclick={() => (player.settings.show = false)}>
					<IconClose />
				</button>
			</div>
			<div class="space-y-3">
				<div>
					<div class="mb-1 flex justify-between font-mono text-xs text-base-content/70">
						<span>Radius</span>
						<span class="font-bold text-primary">{player.settings.discoveryRadiusMeters} meters</span>
					</div>
					<input
						class="range w-full range-primary range-xs"
						type="range"
						min="50"
						max="300"
						step="25"
						bind:value={player.settings.discoveryRadiusMeters}
						oninput={triggerRadarWave}
					/>
				</div>
				<p class="text-xs leading-relaxed text-base-content/70">Landmarks within this radius will pop up on the map.</p>
			</div>
		</div>
	{/snippet}

	{#snippet Telemetry()}
		<div class="flex min-w-0 items-center gap-1.5 font-mono text-xs sm:gap-2">
			<span class="font-bold tracking-wider text-base-content/70"> GPS SIM </span>
			<span class="text-base-content/35">·</span>
			<span class="font-semibold text-base-content">
				{Math.round(displayDistance)}m / {Math.round(sampler.totalDistance)}m
			</span>
		</div>

		<div class="flex shrink-0 items-center gap-2 sm:gap-3">
			{@render SettingsButton()}
			{#if onClose}
				{@render CloseButton()}
			{/if}
		</div>

		{#snippet SettingsButton()}
			<button
				class={{ 'btn btn-circle btn-outline btn-neutral btn-xs': true, 'btn-active': player.settings.show }}
				aria-label="Settings panel"
				title="Settings panel"
				onclick={() => (player.settings.show = !player.settings.show)}
			>
				<IconTune class="text-sm" />
			</button>
		{/snippet}

		{#snippet CloseButton()}
			<button class="btn btn-circle btn-error btn-xs" aria-label="Exit Tour" title="Exit Tour" onclick={onClose}>
				<IconClose />
			</button>
		{/snippet}
	{/snippet}

	{#snippet ScrubberTrack()}
		<input
			class="range w-full range-primary range-xs"
			type="range"
			min="0"
			max={sampler.totalDistance}
			value={displayDistance}
			oninput={(e) => handleSeek(+e.currentTarget.value)}
		/>

		{#each stopCheckpoints as checkpoint (checkpoint.index)}
			{@const isPassed = displayDistance >= checkpoint.distance}

			<button
				type="button"
				class="group/pin absolute top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-transform duration-200 ease-out hover:z-20 hover:scale-125"
				style:left="calc(10px + {checkpoint.progress} * (100% - 20px))"
				title={`Jump to Stop ${checkpoint.index + 1}: ${checkpoint.label}`}
				onclick={(e) => {
					e.stopPropagation();
					handleSeek(checkpoint.distance);
				}}
			>
				<span
					class={{
						'relative flex size-5 items-center justify-center rounded-full font-mono text-xs font-bold transition-all duration-300': true,
						'bg-linear-to-br from-primary to-primary/80 text-primary-content shadow-[0_0_0_2px_var(--color-base-100),0_2px_6px_rgba(0,0,0,0.35)]':
							isPassed,
						'bg-base-200 text-base-content/70 shadow-[0_0_0_2px_var(--color-base-100),0_1px_3px_rgba(0,0,0,0.2)] ring-1 ring-base-content/15 ring-inset':
							!isPassed
					}}
				>
					{checkpoint.index + 1}

					{#if isPassed}
						<span class="absolute inset-0 -z-10 rounded-full bg-primary opacity-40 blur-[3px]"></span>
					{/if}
				</span>
			</button>
		{/each}
	{/snippet}

	{#snippet Controls()}
		<div class="flex w-full shrink-0 items-center justify-between gap-1 sm:gap-1.5 md:w-auto">
			<div>
				{@render PlayButton()}
				{@render SpeedButton()}
			</div>
			{@render HistoryDrawerButtonMobile()}
		</div>

		{#if !deviceScreen.isMobile}
			{@render NextStopSection()}
		{/if}

		{#snippet PlayButton()}
			{const isCurrentDistanceAtEnd = $derived<boolean>(displayDistance >= sampler.totalDistance)}
			<button
				class="btn btn-circle shadow-md btn-primary btn-sm"
				aria-label={player.isPlaying ? 'Pause Journey' : 'Play Journey'}
				onclick={() => {
					if (isCurrentDistanceAtEnd) {
						currentDistance = 0;
					}
					player.isPlaying = !player.isPlaying;
					syncProgressToParent(true);
				}}
			>
				{#if player.isPlaying}
					<IconPause />
				{:else if isCurrentDistanceAtEnd}
					<IconRestart />
				{:else}
					<IconPlay />
				{/if}
			</button>
		{/snippet}

		{#snippet SpeedButton()}
			{const formatPlaybackSpeed = (speed: number) => (speed < 1 ? `${speed.toString().replace(/^0/, '')}x` : `${speed}x`)}

			{#if deviceScreen.isMobile}
				{const nextIndex = $derived<number>(
					(SPEED_MULTIPLIERS.indexOf(player.settings.playbackSpeed) + 1) % SPEED_MULTIPLIERS.length
				)}
				{const nextSpeedMultiplier = $derived<ALLOWED_SPEED>(SPEED_MULTIPLIERS[nextIndex])}
				<button
					class="btn btn-circle btn-ghost font-mono font-bold text-primary btn-xs sm:hidden"
					aria-label="Change speed to {nextSpeedMultiplier}x"
					onclick={() => {
						player.settings.playbackSpeed = nextSpeedMultiplier;
					}}
				>
					{formatPlaybackSpeed(player.settings.playbackSpeed)}
				</button>
			{:else}
				<div class="hidden rounded-full bg-base-100/50 p-0.5 sm:inline-flex">
					{#each SPEED_MULTIPLIERS as mult (mult)}
						<button
							class={{
								'btn rounded-full border-none font-mono font-bold transition-colors btn-primary btn-xs': true,
								'btn-active': player.settings.playbackSpeed === mult,
								'btn-ghost': player.settings.playbackSpeed !== mult
							}}
							onclick={() => (player.settings.playbackSpeed = mult)}
						>
							{formatPlaybackSpeed(mult)}
						</button>
					{/each}
				</div>
			{/if}
		{/snippet}

		{#snippet HistoryDrawerButtonMobile()}
			{#if deviceScreen.isMobile}
				<div class="flex shrink-0 items-center md:hidden">
					<button
						class={{
							'btn gap-1 rounded-full font-mono shadow-md btn-neutral btn-xs': true,
							'btn-active': player.discoveredDrawer.isShowing
						}}
						title="View all discovered landmarks"
						onclick={() => {
							player.discoveredDrawer.isShowing = true;
							player.discoveredDrawer.playerPlayingBeforeShowing = player.isPlaying;
							player.isPlaying = false;
						}}
					>
						<IconHistory />
						<span>{pastDiscoveredPois.length} Found</span>
					</button>
				</div>
			{/if}
		{/snippet}
	{/snippet}

	{#snippet NextStopSection()}
		<div class="min-w-0 overflow-hidden px-1">
			{const nextStop = $derived<StopCheckpoint>(
				stopCheckpoints.find((s) => s.distance >= displayDistance) || stopCheckpoints[stopCheckpoints.length - 1]
			)}
			{#if upcomingLandmark}
				{@render NextLandmarkSection({ label: upcomingLandmark.title, distance: upcomingLandmark.distance })}
			{:else if nextStop}
				{const metersAway = $derived(Math.round(nextStop.distance - coarseDistance))}
				{@render NextLandmarkSection({ label: nextStop.label, distance: metersAway })}
			{/if}
		</div>

		{#snippet NextLandmarkSection(item: { label: string; distance: number })}
			<div class="flex w-full min-w-0 animate-pulse items-center justify-center gap-1 font-mono font-bold">
				<span class="line-clamp-1 text-xs text-base-content">
					{item.label}
				</span>
				<span class="badge shrink-0 badge-xs badge-secondary">
					{item.distance}m
				</span>
			</div>
		{/snippet}
	{/snippet}
{/snippet}

{#snippet MapLandmarkIcon(poi: DiscoveredPoi)}
	{const imageUrl = $derived<string | null>(
		generateSpecificSizeImageUrls({ poi, singleImageSize: 250, multiImagesSize: 250 }).at(0) ?? null
	)}
	{#if imageUrl !== null}
		<WikiMapIcon
			wiki={{
				title: poi.marker.title,
				imageUrl,
				coords: poi.marker.coordinates,
				isPopular: popularLandmarkTitles.has(poi.marker.title)
			}}
			shape="circle"
			isActive={inspection.poi?.marker.title === poi.marker.title}
			onclick={() => inspectLandmark(poi)}
		/>
	{/if}
{/snippet}

{#snippet InspectionModal(poi: DiscoveredPoi)}
	{const details = $derived<DiscoveredPoiDetails | undefined>(poi.details)}
	{const modalImages = $derived<string[]>(
		deviceScreen.isMobile
			? generateSpecificSizeImageUrls({ poi, singleImageSize: 500, multiImagesSize: 500 })
			: generateSpecificSizeImageUrls({ poi, singleImageSize: 960, multiImagesSize: 960 })
	)}

	<dialog bind:this={dialogEl} class="modal" onclose={closeInspection}>
		<div class="modal-box flex flex-col gap-3 overflow-y-auto">
			<div class="flex items-start justify-between gap-3">
				{@render Header()}
			</div>

			{@render ImagesCarousel(modalImages, poi.marker.title, 'h-[50vh]')}

			{#if details?.description}
				<p class="line-clamp-4 text-xs leading-relaxed font-light text-base-content/80 sm:text-sm">
					{details.description}
				</p>
			{/if}

			<div class="flex items-center justify-end gap-2 border-t border-base-content/10 pt-2">
				{@render ActionButtons(poi)}
			</div>
		</div>

		<form method="dialog" class="modal-backdrop">
			<button aria-label="Close and resume">close</button>
		</form>
	</dialog>

	{#snippet Header()}
		<div class="flex min-w-0 flex-col gap-1">
			<span class="badge font-mono badge-xs font-bold badge-accent"> LANDMARK INSPECTION </span>
			<h3 class="line-clamp-3 text-lg text-base-content sm:text-xl">
				{poi.marker.title}
			</h3>
			{#if details?.subtitle}
				<p class="line-clamp-2 text-xs text-base-content/70">{details.subtitle}</p>
			{/if}
		</div>
		<form method="dialog">
			<button class="btn btn-circle btn-ghost text-base-content/70 btn-sm hover:text-base-content" aria-label="Close and resume">
				<IconClose class="text-lg" />
			</button>
		</form>
	{/snippet}
{/snippet}

{#snippet DiscoveredLandmarksDrawer()}
	<div class="flex shrink-0 items-center justify-between p-3 text-base-content md:justify-center">
		{@render Header()}
	</div>

	<div class="divider m-1"></div>

	<div class="flex-1 space-y-3 overflow-x-hidden overflow-y-auto p-3">
		{#if upcomingLandmark}
			{@render UpcomingLandmarkSection(upcomingLandmark)}
		{/if}

		{#if passingByPoi}
			<div
				class="flex shrink-0 cursor-pointer flex-col gap-2 overflow-hidden rounded-box border-2 border-primary/40 bg-base-100 p-3 shadow-md transition-colors hover:border-primary"
				transition:slide={{ duration: 280 }}
				role="button"
				tabindex="0"
				onclick={() => {
					if (deviceScreen.isMobile) {
						toggleExpanded(passingByPoi);
					} else {
						inspectLandmark(passingByPoi);
					}
				}}
				onkeydown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						inspectLandmark(passingByPoi);
					}
				}}
			>
				{#key passingByPoi.marker.title}
					{@render PassingByPoi(passingByPoi)}
				{/key}
			</div>
		{/if}

		<div class="space-y-2 pt-1">
			<h4 class="font-mono text-xs text-base-content/70">DISCOVERED LANDMARKS</h4>

			{#if pastDiscoveredPois.length === 0}
				<div class="p-4 text-center text-xs text-base-content/70">Landmarks will record here as you pass them.</div>
			{:else}
				{#each pastDiscoveredPois as poi (poi.marker.title)}
					{#if poi.marker.title !== passingByPoi?.marker.title}
						{@render PastPoi(poi)}
					{/if}
				{/each}
			{/if}
		</div>
	</div>

	{#snippet Header()}
		<div class="flex items-center gap-2">
			<IconRadar class="animate-pulse " />
			<h3 class="font-mono text-xs">ROUTE JOURNAL</h3>
			<span class="badge font-mono badge-xs badge-secondary">{pastDiscoveredPois.length}</span>
		</div>

		{#if deviceScreen.isMobile}
			<button
				class="btn btn-circle btn-ghost text-base-content btn-xs"
				onclick={() => {
					player.discoveredDrawer.isShowing = false;
					player.isPlaying = player.discoveredDrawer.playerPlayingBeforeShowing;
				}}
				aria-label="Close Journal"
			>
				<IconClose />
			</button>
		{/if}
	{/snippet}

	{#snippet UpcomingLandmarkSection(poi: UpcomingPoi)}
		{const isPopular = $derived<boolean>(popularLandmarkTitles.has(poi.title))}

		<div role="alert" class="alert flex animate-pulse alert-soft alert-success">
			<div class="flex w-full min-w-0 flex-col">
				<div class="flex flex-row items-center justify-between text-xs">
					<div class="flex flex-row gap-1 text-success-content">
						{#if isPopular}
							<IconFlame class="text-highlight" />
						{:else}
							<IconRadar class="text-xs" />
						{/if}
						<span class="font-mono"> APPROACHING </span>
					</div>
					<div class="font-mono text-success-content/70">
						{poi.distance}m
					</div>
				</div>
				<div class="line-clamp-2 text-xs font-bold">{poi.title}</div>
			</div>
		</div>
	{/snippet}

	{#snippet PassingByPoi(poi: DiscoveredPoi)}
		{const isExpanded = $derived<boolean>(expandedPoi === null ? false : expandedPoi.marker.title === poi.marker.title)}
		{const imagesUrls = $derived<string[]>(generateSpecificSizeImageUrls({ poi, singleImageSize: 250, multiImagesSize: 250 }))}
		{const expandedImages = $derived<string[]>(
			generateSpecificSizeImageUrls({ poi, singleImageSize: 330, multiImagesSize: 330 })
		)}

		<div in:fade={{ duration: 220 }} class="flex flex-col gap-2">
			{@render DiscoveredEntryHeader({ poi, title: 'NOW PASSING', isExpanded })}

			{#if !isExpanded}
				<div
					class={{
						'grid h-32 w-full  gap-1 overflow-hidden rounded-box': true,
						'grid-cols-1': imagesUrls.length === 1,
						'grid-cols-2': imagesUrls.length > 1
					}}
					transition:slide={{ duration: 280 }}
					role="none"
					tabindex="-1"
				>
					{#each imagesUrls as img (img)}
						<div class="relative block h-full w-full overflow-hidden bg-base-300">
							<img class="h-full w-full object-cover" src={img} alt="thumb" decoding="async" />
						</div>
					{/each}
				</div>
			{:else}
				<div
					class="flex flex-col gap-2 border-t border-base-content/10"
					transition:slide={{ duration: 280 }}
					role="none"
					tabindex="-1"
					onclick={(e) => e.stopPropagation()}
				>
					{@render ImagesCarousel(expandedImages, poi.marker.title, 'h-[40vh]')}
				</div>
			{/if}

			{#if poi.details?.description}
				<p
					class={{
						'text-xs leading-relaxed font-light text-base-content/70': true,
						'line-clamp-6': isExpanded,
						'line-clamp-3': !isExpanded
					}}
				>
					{poi.details.description}
				</p>
			{/if}

			<div class="flex items-center justify-end gap-2 border-t border-base-content/10 pt-1">
				{@render ActionButtons(poi)}
			</div>
		</div>
	{/snippet}

	{#snippet PastPoi(poi: DiscoveredPoi)}
		{const isExpanded = $derived<boolean>(expandedPoi === null ? false : expandedPoi.marker.title === poi.marker.title)}
		{const isTourStop = $derived<boolean>(poi.stop !== undefined)}
		{const details = $derived<DiscoveredPoiDetails | undefined>(poi.details)}
		{const displayImages = $derived<string[]>(generateSpecificSizeImageUrls({ poi, singleImageSize: 250, multiImagesSize: 250 }))}
		{const expandedImages = $derived<string[]>(
			generateSpecificSizeImageUrls({ poi, singleImageSize: 330, multiImagesSize: 330 })
		)}

		<div
			class={{
				'flex cursor-pointer flex-col gap-1.5 rounded-box p-2.5 transition-all': true,
				'border-2 border-primary/50 bg-primary/10 shadow-sm hover:border-primary hover:bg-primary/15': isTourStop,
				'border border-white/10 bg-base-200/40 hover:border-primary/50 hover:bg-base-200/80': !isTourStop
			}}
			onclick={() => {
				if (deviceScreen.isMobile) {
					toggleExpanded(poi);
				} else {
					inspectLandmark(poi);
				}
			}}
			onkeydown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					inspectLandmark(poi);
				}
			}}
			role="button"
			tabindex="-1"
		>
			{#if isTourStop}
				<div class="flex items-center gap-1">
					<span class="badge font-mono badge-xs font-bold tracking-wider badge-secondary"> OFFICIAL STOP </span>
				</div>
			{/if}

			{@render DiscoveredEntryHeader({ poi, isExpanded })}

			{#if !isExpanded}
				<div class="grid h-12 w-full grid-cols-4 gap-1 overflow-hidden rounded-lg" transition:slide={{ duration: 280 }}>
					{#each displayImages as img, i (img)}
						<div class="block h-full w-full overflow-hidden bg-base-300">
							<img class="h-full w-full object-cover" src={img} alt="thumb" loading="lazy" />
						</div>
					{/each}
				</div>
			{:else}
				<div
					class="flex flex-col gap-2 border-t border-base-content/10"
					transition:slide={{ duration: 280 }}
					role="none"
					tabindex="-1"
					onclick={(e) => e.stopPropagation()}
				>
					{@render ImagesCarousel(expandedImages, poi.marker.title, 'h-[40vh]')}
				</div>
			{/if}

			{#if isExpanded && details?.description}
				<p class="line-clamp-4 text-xs leading-relaxed font-light text-base-content/80">
					{details.description}
				</p>
			{/if}

			{#if deviceScreen.biggerThanMobile  || (deviceScreen.isMobile && isExpanded)}
				<div class="flex items-center justify-end gap-2 pt-1">
					{@render ActionButtons(poi)}
				</div>
			{/if}
		</div>
	{/snippet}

	{#snippet DiscoveredEntryHeader({
		poi,
		title,
		isExpanded
	}: {
		poi: DiscoveredPoi;
		title?: string | undefined;
		isExpanded: boolean;
	})}
		{#if title}
			<div class="flex items-center justify-between gap-1.5">
				<span class="badge font-mono badge-xs font-bold badge-secondary"> {title} </span>
				<div class="flex items-center gap-1">
					{@render LiveDistance()}
					{@render Chevron()}
				</div>
			</div>

			<div class="flex flex-col items-start justify-between gap-1.5">
				<div class="flex min-w-0 flex-col">
					{@render MarkerTitle()}
					{@render Subtitle()}
				</div>
			</div>
		{:else}
			<div class="flex flex-col items-start justify-between gap-1.5">
				<div class="flex w-full flex-row items-center justify-between gap-1">
					{@render MarkerTitle()}
					<div class="flex items-center gap-1">
						{@render LiveDistance()}
						{@render Chevron()}
					</div>
				</div>
				{@render Subtitle()}
			</div>
		{/if}

		{#snippet MarkerTitle()}
			{const isPopular = $derived<boolean>(popularLandmarkTitles.has(poi.marker.title))}

			<h4
				class={{
					'text-sm leading-tight font-semibold text-base-content': true,
					'line-clamp-3': isExpanded,
					'line-clamp-1': !isExpanded
				}}
			>
				{#if isPopular}
					<span class="mr-1 inline-flex items-center align-middle" title="Popular Landmark">
						<IconFlame class="text-xs text-highlight" />
					</span>
				{/if}
				{poi.marker.title}
			</h4>
		{/snippet}

		{#snippet Subtitle()}
			{#if poi.details?.subtitle}
				<i class={{ 'text-xs text-base-content/70': true, 'line-clamp-3': isExpanded, 'line-clamp-1': !isExpanded }}>
					{poi.details.subtitle}
				</i>
			{/if}
		{/snippet}

		{#snippet LiveDistance()}
			<span class="font-mono text-xs text-base-content/70">
				{getLiveDistanceToMarker(poi.marker)}m
			</span>
		{/snippet}

		{#snippet Chevron()}
			{#if deviceScreen.isMobile}
				<IconChevronDown
					class={{ 'text-sm text-base-content/50 transition-transform duration-300': true, 'rotate-180': isExpanded }}
				/>
			{/if}
		{/snippet}
	{/snippet}
{/snippet}

{#snippet ImagesCarousel(images: string[], title: string, heightClass = 'h-40')}
	{let activeIndex = $state<number>(0)}

	<div class="relative flex w-full {heightClass} items-center justify-center overflow-hidden rounded-box bg-base-300/70">
		{#if images.length > 0}
			{#each images as src, i (src)}
				{@render Image(src, i)}
			{/each}

			{#if images.length > 1}
				{@render PreviousImageButton()}
				{@render NextImageButton()}

				<div
					class="pointer-events-none absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1 rounded-full bg-black/50 px-2 py-0.5"
				>
					{#each images as _, i (i)}
						<span class="h-1.5 rounded-full transition-all {i === activeIndex ? 'w-3 bg-white' : 'w-1.5 bg-white/50'}"></span>
					{/each}
				</div>
			{/if}
		{:else}
			<span class="text-xs text-base-content/40">No image available</span>
		{/if}
	</div>

	{#snippet Image(src: string, i: number)}
		<img
			class={{
				'absolute inset-0 h-full w-full object-contain transition-opacity duration-150': true,
				'opacity-100': i === activeIndex,
				'opacity-0': i !== activeIndex
			}}
			{src}
			alt={`${title} - ${i + 1}`}
			loading="eager"
			decoding={i === 0 ? 'sync' : 'async'}
			fetchpriority={i === 0 ? 'high' : 'low'}
			draggable="false"
		/>
	{/snippet}

	{#snippet PreviousImageButton()}
		<button
			class="btn absolute top-1/2 left-2 z-10 btn-circle -translate-y-1/2 border-none bg-black/60 text-white btn-xs hover:bg-black/80"
			onclick={(e) => {
				e.stopPropagation();
				activeIndex = (activeIndex - 1 + images.length) % images.length;
			}}
			aria-label="Previous image"
		>
			<IconChevronLeft class="text-base" />
		</button>
	{/snippet}

	{#snippet NextImageButton()}
		<button
			class="btn absolute top-1/2 right-2 z-10 btn-circle -translate-y-1/2 border-none bg-black/60 text-white btn-xs hover:bg-black/80"
			onclick={(e) => {
				e.stopPropagation();
				activeIndex = (activeIndex + 1) % images.length;
			}}
			aria-label="Next image"
		>
			<IconChevronRight class="text-base" />
		</button>
	{/snippet}
{/snippet}

{#snippet ActionButtons(poi: DiscoveredPoi, onlyGuideLink: boolean = false)}
	{const matchingStop = $derived<TourStop | undefined>(poi.stop)}
	{const hasGuide = $derived<boolean>(Boolean(matchingStop?.primary))}
	{const isWiki = $derived<boolean>(poi.isWikiArticle !== false)}

	<div class="flex w-full flex-col gap-1.5 pt-1">
		{#if hasGuide}
			{const cityFeatureNav = $derived<NavLink>(
				UrlUtil.toCityFeature({
					location: location!,
					type: matchingStop!.primary!.type,
					featureSlug: matchingStop!.primary!.slug
				})
			)}
			<a
				class="group btn flex w-full items-center justify-center gap-1 rounded-full font-mono shadow-sm btn-primary btn-xs"
				href="/{cityFeatureNav.href}"
				data-sveltekit-preload-data={cityFeatureNav.preloadData}
				data-sveltekit-preload-code={cityFeatureNav.preloadCode}
			>
				<span>
					{#if CITY_FEATURE_PAGE_PER_ITEM_TYPES.includes(matchingStop!.primary!.type)}
						view detailed guide
					{:else}
						view stop details
					{/if}
				</span>
				<IconArrowRight class="text-base transition-transform duration-200 group-hover:translate-x-0.5" />
			</a>
		{/if}

		{#if isWiki && !onlyGuideLink}
			<div class="flex w-full items-center justify-end gap-1">
				<a
					class="btn btn-link font-mono btn-neutral btn-xs"
					href={getStreetViewUrl(poi.marker.coordinates)}
					target="_blank"
					rel="noopener noreferrer"
				>
					<IconStreetView class="text-xs" />
					Street View
				</a>
				<a
					class="btn btn-link font-mono btn-neutral btn-xs"
					href={`https://en.wikipedia.org/wiki/${encodeURIComponent(poi.marker.title)}`}
					target="_blank"
					rel="noopener noreferrer"
				>
					<IconWikipedia class="text-xs" />
					Wikipedia
				</a>
			</div>
		{/if}
	</div>
{/snippet}

<style>
	@keyframes sonarRadarWave {
		0% {
			transform: scale(0.05);
			opacity: 0.9;
			border-width: 3px;
		}
		70% {
			opacity: 0.4;
			border-width: 2px;
		}
		100% {
			transform: scale(1);
			opacity: 0;
			border-width: 1px;
		}
	}

	.sonar-wave {
		animation: sonarRadarWave 1.3s cubic-bezier(0.1, 0.4, 0.2, 1) 2 forwards;
	}
</style>
